import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import '../../scss/index.scss';

const SpecialScatterPlot = ({
    data = [],
    fullData = [],
    filteredTableSchedules = new Set(),
    onAreaSelect = () => { },
    hiddenSchedules,
    setHiddenSchedules,
    onResetSelection,
}) => {
    const svgRef = useRef();
    const [selectMode, setSelectMode] = useState(false);

    const quarterRegex = /^Q([1-4])[ -](\d{4})$/;
    const getSortedQuarters = (rows) => {
        const quarters = new Set();
        rows.forEach((item) => {
            const row = item.data || item;
            Object.keys(row).forEach((key) => {
                if (quarterRegex.test(key)) quarters.add(key);
            });
        });
        return Array.from(quarters).sort((a, b) => {
            const [, qA, yA] = a.match(quarterRegex);
            const [, qB, yB] = b.match(quarterRegex);
            return yA !== yB ? yA - yB : qA - qB;
        });
    };

    const visibleSchedules = new Set(data.map((d) => d.data?.Schedule).filter(Boolean));

    const scheduleColorMap = React.useMemo(() => {
        const map = new Map();
        fullData.forEach((d) => {
            const schedule = d.data?.Schedule;
            const color = d.data?.Color || '#888';
            if (schedule && !map.has(schedule)) {
                map.set(schedule, color);
            }
        });
        return map;
    }, [fullData]);

    const legendSchedulesArray = React.useMemo(
        () =>
            Array.from(scheduleColorMap.entries()).map(([schedule, color]) => ({
                schedule,
                color,
            })),
        [scheduleColorMap],
    );

    const filteredLegendSchedulesArray = React.useMemo(() => {
        return legendSchedulesArray.filter(({ schedule }) =>
            visibleSchedules.has(schedule),
        );
    }, [legendSchedulesArray, visibleSchedules]);

    const categories = React.useMemo(
        () => ['Consistent Zeroes', 'New', 'Consistent Non-Zeroes', 'Dropped'],
        [],
    );

    const filtered = data
        .map((d) => d.data)
        .filter((d) => d.Category && categories.includes(d.Category));

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = {
            top: 40,
            right: 40,
            bottom: 85,
            left: 8,
        };
        const width = 600;
        const height = 600;
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;

        //if (filtered.length === 0) return;

        const allQuarters = getSortedQuarters(filtered);

        const x = d3.scalePoint().domain(categories).range([0, plotWidth]).padding(0.4);

        const y = d3
            .scaleLinear()
            .domain([0, filtered.length + 1])
            .range([0, plotHeight]);

        const g = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        g.append('g')
            .attr('transform', `translate(0,${plotHeight})`)
            .call(d3.axisBottom(x))
            .selectAll('text')
            .style('font-size', '11px');

        g.append('line')
            .attr('x1', 0)
            .attr('x2', 0)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#ccc')
            .attr('stroke-width', 1);

        const tooltip = d3
            .select('body')
            .append('div')
            .attr('class', 'tooltip')
            .style('position', 'absolute')
            .style('padding', '8px 10px')
            .style('font', '12px sans-serif')
            .style('background', '#333')
            .style('color', '#fff')
            .style('border-radius', '5px')
            .style('pointer-events', 'none')
            .style('opacity', 0);

        // Position map to reverse-lookup on brush
        const pointMap = [];

        const displayedData = filtered.filter((d) => !hiddenSchedules.has(d.Schedule));

        displayedData.forEach((d, i) => {
            const cx = x(d.Category);
            const cy = y(i + 1);
            pointMap.push({ mdrm: d.MDRM, x: cx, y: cy });

            g.append('circle')
                .attr('cx', cx)
                .attr('cy', cy)
                .attr('r', 4)
                .attr('fill', scheduleColorMap.get(d.Schedule) || '#888')
                .on('mouseover', function (event) {
                    d3.select(this).attr('r', 6);
                    const quarterHTML = allQuarters
                        .filter((q) => d[q] !== undefined)
                        .map((q) => `<div>${q}: ${d[q]}</div>`)
                        .join('');
                    tooltip
                        .style('opacity', 1)
                        .html(`<strong>${d.MDRM}</strong><br/>${quarterHTML}`)
                        .style('left', `${event.pageX + 10}px`)
                        .style('top', `${event.pageY - 28}px`);
                })
                .on('mouseout', function () {
                    d3.select(this).attr('r', 4);
                    tooltip.style('opacity', 0);
                });
        });

        // Brush Selection
        if (selectMode) {
            const brush = d3
                .brush()
                .extent([
                    [0, 0],
                    [plotWidth, plotHeight],
                ])
                .on('end', (event) => {
                    const { selection } = event;
                    if (!selection) {
                        onAreaSelect(null);
                        return;
                    }
                    const [[x0, y0], [x1, y1]] = selection;
                    const selected = pointMap
                        .filter((p) => p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1)
                        .map((p) => p.mdrm);
                    onAreaSelect(selected);
                });

            g.append('g').call(brush);
        }

        return () => d3.select('.tooltip').remove();
    }, [data, selectMode, onAreaSelect, hiddenSchedules]);

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <div className="area-selection-header">
                <label htmlFor="enable-select-mode" className="checkbox-label">
                    <input
                        id="enable-select-mode"
                        type="checkbox"
                        checked={selectMode}
                        onChange={() => setSelectMode((prev) => !prev)}
                    />
                    Enable Area Selection
                </label>

                <button
                    type="button"
                    className="clear-all"
                    onClick={onResetSelection}
                >
                    Reset Selection
                </button>
            </div>
            <svg ref={svgRef} style={{ width: '100%', height: '600px' }} />
            <div className="ss-legends-wrapper">
                <strong className="ss-legend-title">Legend:</strong>
                <div className="ss-schedules-wrapper">
                    {legendSchedulesArray.map(({ schedule, color }) => {
                        const isInFilteredTable = filteredTableSchedules.has(schedule);
                        const isUserHidden = hiddenSchedules.has(schedule);

                        const isVisuallyDisabled = isUserHidden || !isInFilteredTable;

                        return (
                            <div
                                key={schedule}
                                className="ss-schedules"
                                style={{
                                    cursor: 'pointer',
                                    opacity: isVisuallyDisabled ? 0.5 : 1,
                                    userSelect: 'none',
                                }}
                                onClick={() => {
                                    setHiddenSchedules((prev) => {
                                        const copy = new Set(prev);
                                        if (copy.has(schedule)) copy.delete(schedule);
                                        else copy.add(schedule);
                                        return copy;
                                    });
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setHiddenSchedules((prev) => {
                                            const copy = new Set(prev);
                                            if (copy.has(schedule)) copy.delete(schedule);
                                            else copy.add(schedule);
                                            return copy;
                                        });
                                    }
                                }}
                            >
                                <div
                                    className="ss-schedule"
                                    style={{
                                        backgroundColor: isUserHidden ? '#ccc' : color,
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 11,
                                        textDecoration: isVisuallyDisabled
                                            ? 'line-through'
                                            : 'none',
                                        color: isVisuallyDisabled ? '#999' : 'inherit',
                                    }}
                                >
                                    {schedule}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SpecialScatterPlot;
